import React, { useState, useRef } from 'react';
import { DocumentsProps, Document } from '../../../types/expertise-detail.types';
import { uploadDocument } from '../../../api/expertise-detail.api';
import { formatDate, areDocumentTitlesSimilar } from '../../../utils/formatters';
import { Loading } from '@/ui/components/atoms/Loading';

export const Documents: React.FC<DocumentsProps> = ({ affaireId, documents }) => {
  const [uploadingDocs, setUploadingDocs] = useState<Set<string>>(new Set());
  const [uploadErrors, setUploadErrors] = useState<Map<string, string>>(new Map());
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  if (!documents) {
    return (
      <div className="documents-container loading">
        <Loading size="medium" text="Chargement des documents..." />
      </div>
    );
  }

  // Filter out documents that have already been provided (fuzzy matching)
  const getFilteredRequestedDocuments = () => {
    const requested = documents.requested_documents.documents;
    const checked = documents.checked_documents.documents;
    
    return requested.filter(reqDoc => {
      // Check if this document has already been provided
      const isProvided = checked.some(checkDoc => 
        areDocumentTitlesSimilar(reqDoc.intitule_document, checkDoc.intitule_document)
      );
      return !isProvided;
    });
  };

  const filteredRequestedDocs = getFilteredRequestedDocuments();

  const handleFileSelect = async (docId: string, file: File) => {
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setUploadErrors(prev => new Map(prev).set(docId, 'Le fichier ne doit pas dépasser 10MB'));
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      setUploadErrors(prev => new Map(prev).set(docId, 'Format non supporté. Utilisez PDF, JPG ou PNG'));
      return;
    }

    // Clear previous error
    setUploadErrors(prev => {
      const newErrors = new Map(prev);
      newErrors.delete(docId);
      return newErrors;
    });

    // Start upload
    setUploadingDocs(prev => new Set(prev).add(docId));

    try {
      const response = await uploadDocument(affaireId, docId, file);
      
      if (response.success) {
        // Success - document will be moved to checked list on next refresh
        // Clear the file input
        const input = fileInputRefs.current.get(docId);
        if (input) {
          input.value = '';
        }
      } else {
        setUploadErrors(prev => new Map(prev).set(docId, response.message || 'Erreur lors du téléchargement'));
      }
    } catch (error) {
      setUploadErrors(prev => new Map(prev).set(docId, 'Erreur de connexion'));
    } finally {
      setUploadingDocs(prev => {
        const newSet = new Set(prev);
        newSet.delete(docId);
        return newSet;
      });
    }
  };

  const getDocumentStatus = (doc: Document) => {
    if (uploadingDocs.has(doc.id_unique_doc)) {
      return 'uploading';
    }
    if (uploadErrors.has(doc.id_unique_doc)) {
      return 'error';
    }
    if (doc.date_de_reception) {
      return 'received';
    }
    return 'pending';
  };

  return (
    <div className="documents-container">
      <div className="documents-header">
        <h2>Gestion des documents</h2>
        <div className="documents-stats">
          <span className="stat">
            <span className="stat-value">{filteredRequestedDocs.length}</span>
            <span className="stat-label">En attente</span>
          </span>
          <span className="stat">
            <span className="stat-value">{documents.checked_documents.documents.length}</span>
            <span className="stat-label">Vérifiés</span>
          </span>
        </div>
      </div>

      <div className="documents-grid">
        {/* Requested Documents Section */}
        <div className="documents-section requested">
          <h3>Documents demandés</h3>
          {filteredRequestedDocs.length > 0 ? (
            <div className="documents-list">
              {filteredRequestedDocs.map((doc) => {
                const status = getDocumentStatus(doc);
                const error = uploadErrors.get(doc.id_unique_doc);
                
                return (
                  <div key={doc.id_unique_doc} className={`document-item ${status}`}>
                    <div className="document-info">
                      <span className="document-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <div className="document-details">
                        <p className="document-name">{doc.intitule_document}</p>
                        {doc.date_de_reception && (
                          <p className="document-date">Demandé le {formatDate(doc.date_de_reception)}</p>
                        )}
                        {error && <p className="document-error">{error}</p>}
                      </div>
                    </div>
                    
                    <div className="document-action">
                      {status === 'uploading' ? (
                        <div className="upload-progress">
                          <Loading size="small" />
                        </div>
                      ) : (
                        <>
                          <input
                            ref={(el) => {
                              if (el) fileInputRefs.current.set(doc.id_unique_doc, el);
                            }}
                            type="file"
                            id={`file-${doc.id_unique_doc}`}
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileSelect(doc.id_unique_doc, file);
                            }}
                            className="file-input"
                          />
                          <label htmlFor={`file-${doc.id_unique_doc}`} className="upload-button">
                            <span className="upload-icon">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                            <span>Télécharger</span>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucun document en attente</p>
              <p className="hint">Tous les documents demandés ont été fournis</p>
            </div>
          )}
        </div>

        {/* Checked Documents Section */}
        <div className="documents-section checked">
          <h3>Documents vérifiés</h3>
          {documents.checked_documents.documents.length > 0 ? (
            <div className="documents-list">
              {documents.checked_documents.documents.map((doc) => (
                <div key={doc.id_unique_doc} className="document-item verified">
                  <div className="document-info">
                    <span className="document-icon verified">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M9 11l3 3L22 4" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <div className="document-details">
                      <p className="document-name">{doc.intitule_document}</p>
                      {doc.date_de_reception && (
                        <p className="document-date">Vérifié le {formatDate(doc.date_de_reception)}</p>
                      )}
                    </div>
                  </div>
                  <div className="document-status">
                    <span className="status-badge verified">Vérifié</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Aucun document vérifié</p>
              <p className="hint">Les documents vérifiés apparaîtront ici</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .documents-container {
          padding: 24px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
        }

        .documents-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }



        .documents-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding-bottom: 20px;
          border-bottom: 1px solid #E5E7EB;
        }

        .documents-header h2 {
          margin: 0;
          font-size: 22px;
          font-weight: 600;
          color: #002035;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .documents-stats {
          display: flex;
          gap: 24px;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          color: #002035;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .stat-label {
          font-size: 12px;
          color: #6B7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .documents-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
        }

        .documents-section h3 {
          font-size: 16px;
          font-weight: 600;
          color: #002035;
          margin: 0 0 20px 0;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .documents-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .document-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #F8F9FA;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .document-item:hover {
          background: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transform: translateX(2px);
        }

        .document-item.verified {
          background: #F0FDF4;
          border-color: #86EFAC;
        }

        .document-item.uploading {
          background: #FEF3C7;
          border-color: #FDE68A;
        }

        .document-item.error {
          background: #FEF2F2;
          border-color: #FECACA;
        }

        .document-info {
          display: flex;
          gap: 12px;
          align-items: center;
          flex: 1;
        }

        .document-icon {
          font-size: 24px;
        }

        .document-icon.verified {
          font-size: 20px;
        }

        .document-details {
          flex: 1;
        }

        .document-name {
          margin: 0;
          font-weight: 500;
          color: #002035;
          font-size: 14px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .document-date {
          margin: 4px 0 0 0;
          font-size: 12px;
          color: #6B7280;
        }

        .document-error {
          margin: 4px 0 0 0;
          font-size: 12px;
          color: #ef5343;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .document-action {
          display: flex;
          align-items: center;
        }

        .file-input {
          display: none;
        }

        .upload-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          background: white;
          border: 1px solid #D1D5DB;
          border-radius: 50px;
          color: #002035;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .upload-button:hover {
          background: #ef5343;
          border-color: #ef5343;
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 83, 67, 0.3);
        }

        .upload-icon {
          font-size: 16px;
        }

        .upload-progress {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #92400E;
          font-size: 14px;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .status-badge.verified {
          background: #D1FAE5;
          color: #065F46;
        }

        .empty-state {
          padding: 40px 20px;
          text-align: center;
          background: #F8F9FA;
          border: 2px dashed #E5E7EB;
          border-radius: 12px;
          color: #6B7280;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .empty-state p {
          margin: 0;
        }

        .hint {
          font-size: 13px;
          color: #9CA3AF;
          margin-top: 8px;
        }

        @media (max-width: 1024px) {
          .documents-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .documents-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .documents-stats {
            width: 100%;
            justify-content: space-around;
          }

          .document-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .document-action {
            width: 100%;
          }

          .upload-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};